import React from "react";
import { Shield, Eye, Lock, Database, FileText, Mail } from "lucide-react";

const Privacy = () => {
  const sections = [
    {
      id: "collect",
      icon: Eye,
      title: "Information We Collect",
      desc: "We collect specific information to offer high-quality internships and verified credential programs:",
      items: [
        { title: "Personal Details", desc: "Name, email address, phone number, and academic background." },
        { title: "Application Logs", desc: "Selected internship domains, task submissions, and project history." },
        { title: "Platform Usage", desc: "Device attributes, interaction logs, and preferred landing areas." },
        { title: "Access Security", desc: "Password verification hashes and login session tokens." }
      ]
    },
    {
      id: "use",
      icon: Lock,
      title: "How We Use Your Information",
      desc: "Your data is handled with maximum care to deliver and support our core education services:",
      items: [
        { title: "Matching Projects", desc: "Assigning relevant tasks and training cohorts based on your skills." },
        { title: "Communication", desc: "Sending progress reports, certificate issue alerts, and login links." },
        { title: "UI & UX Refining", desc: "Analyzing interaction logs to resolve speed bottlenecks and UI errors." },
        { title: "Direct Mentorship", desc: "Granting access to review systems and community chat systems." }
      ]
    },
    {
      id: "sharing",
      icon: Database,
      title: "Data Sharing & Protection",
      desc: "We prioritize security measures to ensure data safety across all database instances:",
      items: [
        { title: "Zero Data Selling", desc: "We never monetize, trade, or share your profile details with advertisers." },
        { title: "Secure Transmission", desc: "All system traffic is encrypted using industry-standard SSL certificates." },
        { title: "Admin Access Control", desc: "Server credentials are only accessible by audited core administrators." },
        { title: "Frequent Audits", desc: "Running safety patches and dependency upgrades to check system safety." }
      ]
    },
    {
      id: "rights",
      icon: FileText,
      title: "Your Rights & Control",
      desc: "You retain total control over the personal records you share with Student Era:",
      items: [
        { title: "Data Portability", desc: "Request and download a structured export file of your credentials." },
        { title: "Instant Correction", desc: "Modify or update your profile records at any time via your dashboard." },
        { title: "Account Deletion", desc: "Submit a request to completely purge your profile and files from our servers." },
        { title: "Verification Toggle", desc: "Choose whether your certificate verification is publicly searchable." }
      ]
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-50 py-16 sm:py-24 px-4 sm:px-6 lg:px-8 font-sans">
      {/* Background Decorative Blobs */}
      <div className="absolute top-1/4 -left-32 w-72 sm:w-96 h-72 sm:h-96 bg-indigo-200/40 rounded-full mix-blend-multiply filter blur-3xl opacity-60 animate-pulse pointer-events-none"></div>
      <div className="absolute bottom-1/4 -right-32 w-72 sm:w-96 h-72 sm:h-96 bg-purple-200/40 rounded-full mix-blend-multiply filter blur-3xl opacity-60 animate-pulse delay-1000 pointer-events-none"></div>

      <div className="relative max-w-5xl mx-auto z-10">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-center mb-4 text-gradient-premium">
            Privacy Policy
          </h1>
          <p className="text-slate-500 text-base sm:text-lg max-w-2xl mx-auto font-medium">
            We are committed to protecting your personal information and being transparent about our data practices.
          </p>
        </div>

        {/* Commitment Section (Full Width) */}
        <div className="glass-card rounded-3xl p-6 sm:p-8 shadow-xl shadow-slate-100/50 border border-white/60 mb-8 flex flex-col sm:flex-row items-start gap-6">
          <div className="flex-shrink-0 bg-indigo-50 text-indigo-600 border border-indigo-100/80 p-3 rounded-2xl shadow-sm">
            <Shield className="h-7 w-7" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800 mb-3 tracking-tight">Our Commitment to Privacy</h2>
            <p className="text-slate-600 leading-relaxed text-base">
              At Student Era, your privacy is our top priority. We recognize the trust you place in us when sharing your educational records and personal information. We are committed to employing robust encryption protocols, restricting access privileges, and maintaining transparency about how we collect, process, and safeguard your data throughout your learning journey.
            </p>
          </div>
        </div>

        {/* Policy Grid (2-column on desktop) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {sections.map((section) => {
            const SectionIcon = section.icon;
            return (
              <div
                key={section.id}
                className="glass-card rounded-3xl p-6 sm:p-8 shadow-xl shadow-slate-100/50 border border-white/60 flex flex-col h-full hover:border-indigo-300/40 hover:shadow-2xl hover:shadow-indigo-100/10 transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-shrink-0 bg-indigo-50 text-indigo-600 border border-indigo-100/80 p-2.5 rounded-xl">
                    <SectionIcon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 tracking-tight">{section.title}</h3>
                </div>

                <p className="text-slate-500 text-sm mb-5 font-medium">{section.desc}</p>

                <div className="space-y-3.5 mt-auto">
                  {section.items.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 bg-white/60 hover:bg-white border border-slate-100 rounded-2xl p-4 transition-all duration-200 shadow-sm"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2 shrink-0"></div>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        <strong className="text-slate-850 font-extrabold block mb-0.5">{item.title}</strong>
                        {item.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact Support Section (Full Width) */}
        <div className="glass-card rounded-3xl p-6 sm:p-10 shadow-xl shadow-slate-100/50 border border-white/60 text-center flex flex-col items-center">
          <div className="bg-indigo-50 text-indigo-600 border border-indigo-100/80 p-4 rounded-3xl mb-5 shadow-sm">
            <Mail className="h-8 w-8" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-3 tracking-tight">Questions or Data Requests?</h2>
          <p className="text-slate-500 text-sm sm:text-base max-w-xl mb-6 font-medium leading-relaxed">
            If you have any questions about our privacy policies, want to request copies of your data records, or need to purge your files, please reach out to our privacy officer.
          </p>

          {/* Premium Interactive Button with Glowing Border */}
          <a
            href="mailto:contact@studentera.online"
            className="inline-flex items-center gap-2.5 px-6 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl shadow-sm hover:shadow active:scale-95 transition-all duration-200 text-sm sm:text-base"
          >
            <Mail className="h-5 w-5 text-indigo-200" />
            <span>contact@studentera.online</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
